<template>
  <div :class="wrapperClass">
    <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-0 overflow-hidden">
      <slot name="tabs"></slot>

      <div
        v-if="showHeader"
        class="dt-header flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-200"
      >
        <!-- Search -->
        <div class="w-64">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search"
              class="w-full h-9 rounded-lg border border-gray-300 px-9 text-sm focus:outline-none focus:ring-0"
            />
          </div>
        </div>

        <!-- Per page -->
        <div class="flex-1 flex justify-center">
          <label class="inline-flex items-center gap-2 text-sm text-gray-600">
            <select
              v-model.number="rowsPerPageLocal"
              class="h-9 rounded-md border border-gray-300 px-5 text-sm focus:outline-none"
            >
              <option v-for="opt in rowsPerPageOptions" :key="opt" :value="opt">
                {{ opt }} per page
              </option>
            </select>
          </label>
        </div>

        <!-- Optional Add button -->
        <div class="w-64 flex justify-end">
          <button
            v-if="showAddButton"
            class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold bg-[#5B9AC5] text-white text-sm shadow-sm hover:opacity-95"
            @click="handleAddClick"
          >
            {{ addButtonLabel }}
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="w-full">
        <div class="overflow-x-auto">
          <table class="min-w-[640px] sm:min-w-full sm:table-fixed">
            <thead>
              <tr class="text-xs text-gray-600 tracking-tight">
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="whitespace-nowrap px-3 py-2 h-[50px] text-base border-b border-gray-200 font-medium"
                  :class="alignClass(column)"
                  :style="column.width ? { width: column.width } : null"
                >
                  <div
                    v-if="column.selectable"
                    class="flex items-center justify-center gap-2"
                  >
                    <label for="select-all">{{ column.label }}</label>
                  </div>
                  <span v-else>{{ column.label }}</span>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in paginatedRows"
                :key="row.id ?? row.bookingId ?? JSON.stringify(row)"
                :class="[rowClassProp(row), 'group text-xs']"
              >
                <td
                  v-for="column in columns"
                  :key="column.key"
                  class="px-3 py-2 h-[50px] border-b border-gray-200 align-middle"
                  :class="alignClass(column)"
                  :style="column.width ? { width: column.width } : null"
                >
                  <div
                    class="truncate"
                    :style="column.maxWidth ? { maxWidth: column.maxWidth } : null"
                  >
                    <template v-if="$slots[column.key]">
                      <slot :name="column.key" :value="row[column.key]" :row="row" />
                    </template>
                    <span v-else>{{ row[column.key] }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="flex justify-center items-center gap-2 py-5">
        <button
          v-if="currentPage > 1"
          @click="changePage(currentPage - 1)"
          class="w-9 h-9 rounded-full hover:bg-gray-100 transition"
          aria-label="Previous"
        >
          ‹
        </button>

        <ul class="flex gap-2">
          <li v-for="page in totalPages" :key="page">
            <a
              href="#"
              @click.prevent="changePage(page)"
              :class="[
                'inline-flex items-center justify-center w-9 h-9 rounded-full text-sm',
                page === currentPage
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              {{ page }}
            </a>
          </li>
        </ul>

        <button
          v-if="currentPage < totalPages"
          @click="changePage(currentPage + 1)"
          class="w-9 h-9 rounded-full hover:bg-gray-100 transition"
          aria-label="Next"
        >
          ›
        </button>
      </div>
    </div>

    <!-- Actions Modal -->
    <div
      v-if="showActionsModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div class="bg-white w-96 p-6 rounded-lg shadow-lg space-y-4">
        <h3 class="text-lg font-bold text-center">Actions</h3>
        <div class="grid grid-cols-2 gap-3">
          <button
            v-for="action in actions"
            :key="action.name"
            @click="action.handler(selectedRow)"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
          >
            {{ action.name }}
          </button>
        </div>
        <button
          @click="showActionsModal = false"
          class="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DynamicTable",
  props: {
    title: { type: String, default: "Table" },
    wrapperClass: { type: String, default: "max-w-full" },
    rows: { type: Array, required: true },
    columns: { type: Array, required: true },
    actions: { type: Array, default: () => [] },
    rowsPerPage: { type: Number, default: 50 },
    rowsPerPageOptions: { type: Array, default: () => [10, 20, 50] },
    rowClass: { type: Function, default: () => '' },
    showAddButton: { type: Boolean, default: false },
    addButtonLabel: { type: String, default: 'New' },
    onAddButtonClick: { type: Function, default: null },
    showHeader: { type: Boolean, default: true },
    externalSearch: { type: String, default: null },
  },
  emits: ['add'],
  data() {
    return {
      searchQuery: "",
      selectedRow: null,
      showActionsModal: false,
      currentPage: 1,
      rowsPerPageLocal: this.rowsPerPage,
    };
  },
  computed: {
    searchKeys() {
      return (this.columns || [])
        .map(c => c.key)
        .filter(k => k && k !== 'action');
    },
    effectiveSearch() {
      return (this.externalSearch ?? this.searchQuery).trim().toLowerCase();
    },
    searchedRows() {
      const q = this.effectiveSearch;
      if (!q) return this.rows;
      return this.rows.filter(row => this.matchesQuery(row, q));
    },
    paginatedRows() {
      const start = (this.currentPage - 1) * this.rowsPerPageLocal;
      return this.searchedRows.slice(start, start + this.rowsPerPageLocal);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.searchedRows.length / this.rowsPerPageLocal));
    },
  },
  watch: {
    rowsPerPage(newVal) {
      if (newVal !== this.rowsPerPageLocal) {
        this.rowsPerPageLocal = newVal;
        this.currentPage = 1;
      }
    },
    rowsPerPageLocal() { this.currentPage = 1; },
    searchQuery() { this.currentPage = 1; },
    externalSearch() { this.currentPage = 1; },
  },
  methods: {
    // ✅ default center (header matches data alignment)
    alignClass(column) {
      const a = (column?.align || 'center').toLowerCase();
      if (a === 'left') return 'text-left';
      if (a === 'right') return 'text-right';
      return 'text-center';
    },

    handleAddClick() {
      if (typeof this.onAddButtonClick === 'function') {
        this.onAddButtonClick();
      } else {
        this.$emit('add');
      }
    },
    flattenValue(val) {
      if (val == null) return '';
      if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        return String(val);
      }
      if (Array.isArray(val)) {
        return val.map(v => this.flattenValue(v)).join(' ');
      }
      if (typeof val === 'object') {
        return Object.values(val).map(v => this.flattenValue(v)).join(' ');
      }
      return '';
    },
    matchesQuery(row, q) {
      const haystack = this.searchKeys
        .map(k => this.flattenValue(row[k]))
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    },
    toggleSelectAll(e) {
      this.selectedRows = e.target.checked ? this.rows.map((r) => r.id) : [];
    },
    changePage(p) {
      this.currentPage = p;
    },
    showActions(row) {
      if (this.actions.length > 0) {
        this.selectedRow = row;
        this.showActionsModal = true;
      }
    },
    closeModal() {
      this.showActionsModal = false;
    },
    rowClassProp(row) {
      return this.rowClass && typeof this.rowClass === "function" ? this.rowClass(row) : "";
    },
  },
};
</script>

<style scoped>
  @reference "tailwindcss";
:deep(td) {
  @apply text-base;
}

@media (max-width: 640px) {
  .dt-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .dt-header > div {
    width: 100% !important;
  }

  .dt-header .w-64,
  .dt-header .flex-1 {
    width: 100% !important;
    justify-content: flex-start;
  }

  :deep(th),
  :deep(td) {
    @apply text-sm;
  }
}
</style>
